/**
 * Which prompt a suggestion edits, what its approval makes, and the note
 * sent to the org to approve it.
 */
import { type FC, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { Button, Dialog, Paragraph } from "@fronterahealth/frontera-ui-components";

import { type PromptSuggestion, useListPromptSuggestionTests } from "@api/rest/types-and-hooks";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import {
  SUGGESTION_KIND_LABELS,
  SUGGESTION_TEST_POLL_MS,
  TEST_VERDICT_LABELS,
  approvalEffect,
  customerExample,
  testInFlight,
} from "@pages/Evaluations/promptSuggestionFields";

/** The kind's label and what approving does, on top of the suggestion. */
export const SuggestionKindNote: FC<{ suggestion: PromptSuggestion; orgName: string }> = ({ suggestion, orgName }) => (
  <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-text-secondary">
    <span className="rounded border border-blurple-200 bg-bg-surface px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blurple-600">
      {SUGGESTION_KIND_LABELS[suggestion.kind]}
    </span>
    {approvalEffect(suggestion, orgName)}
  </p>
);

/** An approved org copy's prompt copy and the template drafts pointed at it.
 *  Renders nothing for the other kinds. */
export const SuggestionOutcomeLinks: FC<{ suggestion: PromptSuggestion }> = ({ suggestion }) => {
  if (!suggestion.copy_prompt_key) return null;
  return (
    <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-text-secondary">
      Org copy:
      <Link
        to={`/prompts/${encodeURIComponent(suggestion.copy_prompt_key)}/evaluations`}
        className="font-mono underline hover:text-text-primary"
      >
        {suggestion.copy_prompt_key}
      </Link>
      {suggestion.template_drafts.length > 0 ? (
        <>
          <span>Template drafts:</span>
          {suggestion.template_drafts.map((draft) => (
            <Link
              key={draft.draft_template_id}
              to={`/templates/${draft.draft_template_id}`}
              title={`Draft of ${draft.source_template_name}`}
              className="underline hover:text-text-primary"
            >
              {draft.draft_template_name} ({draft.draft_template_status})
            </Link>
          ))}
        </>
      ) : null}
    </span>
  );
};

/** Opens the suggestion's customer summary, to read and copy into a message
 *  to the org. */
export const CustomerSummaryButton: FC<{ suggestion: PromptSuggestion }> = ({ suggestion }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        text="Summary for the customer"
        variant="secondary"
        buttonStyle="border"
        size="sm"
        onClick={() => setOpen(true)}
      />
      {open ? <CustomerSummaryDialog suggestion={suggestion} onClose={() => setOpen(false)} /> : null}
    </>
  );
};

/** Mounted only while open, so it starts from the latest summary. */
const CustomerSummaryDialog: FC<{ suggestion: PromptSuggestion; onClose: () => void }> = ({ suggestion, onClose }) => {
  const [text, setText] = useState(suggestion.customer_summary);
  const { data } = useListPromptSuggestionTests(suggestion.id, {
    query: {
      // A test's example shows once the judge has quoted it.
      refetchInterval: (query) => {
        const resp = query.state.data;
        return resp?.status === 200 && resp.data.tests.some((t) => testInFlight(t)) ? SUGGESTION_TEST_POLL_MS : false;
      },
    },
  });
  const judging = data?.status === 200 && data.data.tests.some((t) => testInFlight(t));
  const examples = useMemo(
    () =>
      (data?.status === 200 ? data.data.tests : []).flatMap((test) => {
        const example = customerExample(test);
        return example ? [{ test, example }] : [];
      }),
    [data],
  );

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      notifySuccess("Summary copied.");
      onClose();
    } catch {
      notifyError("Couldn't copy the summary. Select the text and copy it instead.");
    }
  };

  return (
    <Dialog
      open
      setOpen={(next) => {
        if (!next) onClose();
      }}
      title={`Summary for ${suggestion.organization_name}`}
      size="lg"
      primaryButton={{ text: "Copy", onClick: () => void copy() }}
      secondaryButton={{ text: "Close", onClick: onClose }}
    >
      <div className="flex flex-col gap-3">
        <Paragraph size="small" colorType="secondary">
          Plain-language summary of the change, to send {suggestion.organization_name} for approval. Read it through
          first: Rio wrote the theme from clinicians&apos; edits. You can edit it here before copying.
        </Paragraph>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={12}
          aria-label="Customer summary"
          className="w-full rounded-md border border-border-subtle bg-bg-default p-3 text-sm text-text-primary"
        />
        <div className="flex flex-col gap-1">
          <span className="text-xs font-semibold uppercase tracking-wide text-text-secondary">
            Add a before/after example
          </span>
          {examples.length === 0 ? (
            <span className="text-xs text-text-tertiary">
              {data === undefined
                ? "Loading tests…"
                : judging
                  ? "A test is still running or being judged. Its example shows here when it's done."
                  : "No test shows the change yet. Run one from Review & test to add an example."}
            </span>
          ) : (
            <select
              value=""
              onChange={(e) => {
                const chosen = examples.find(({ test }) => String(test.id) === e.target.value);
                if (chosen) setText((prev) => `${prev.trimEnd()}\n\n${chosen.example}`);
              }}
              className="self-start rounded-md border border-border-medium bg-bg-default px-2 py-1 text-xs text-text-primary"
            >
              <option value="">From a test…</option>
              {examples.map(({ test }) => (
                <option key={test.id} value={test.id}>
                  {test.learner_test_profile_name ?? "No profile"} · {new Date(test.created_date).toLocaleDateString()}{" "}
                  · {TEST_VERDICT_LABELS[test.pairwise_verdict ?? ""] ?? "No verdict"}
                </option>
              ))}
            </select>
          )}
          <span className="text-xs text-text-tertiary">
            Quotes come from the test&apos;s outputs on a made-up learner. Check none come from a real uploaded document
            before sending.
          </span>
        </div>
      </div>
    </Dialog>
  );
};
